/**
 * SF-160: mobile Overview tab content — new layout, not a port. Surfaces only the genuinely new
 * content (building efficiency, per-building bonus text); does not re-render either planner —
 * both datasets already have a mobile home elsewhere (MobileBuildingsShell's "Under Construction"
 * card on the Build tab; MobileExploreShell's "In Transit" card on the Explore page).
 */

import { Link } from 'react-router-dom';
import type { Province } from '../../types/province';
import { BUILDING_GROUPS } from '../../constants/buildings';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { GlanceCard } from '../shell/GlanceCard';
import { BUILDING_BONUS } from './buildingBonuses';

interface MobileBuildingOverviewPanelProps {
  province: Province;
}

export function MobileBuildingOverviewPanel({ province: p }: MobileBuildingOverviewPanelProps) {
  const be = p.economy.buildingEfficiency;
  const totalAcres = p.territory.totals.acres;
  const emp = p.economy.employment;

  return (
    <div className="flex flex-col gap-3">
      <GlanceCard title="Efficiency & Workforce">
        <div className="flex items-baseline gap-2.5">
          <span className="font-mono text-3xl font-bold" style={{ color: COLORS.text.primary }}>
            {(be * 100).toFixed(1)}%
          </span>
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>building efficiency</span>
        </div>
        <div className="text-xs" style={{ color: COLORS.text.secondary }}>
          {formatNumber(emp.employed)} workers · {(emp.employmentRate * 100).toFixed(0)}% employed
        </div>
      </GlanceCard>

      {BUILDING_GROUPS.map((group) => {
        const ownedBuildings = group.buildings.filter((b) => (p.territory.buildings[b.id] ?? 0) > 0);
        if (ownedBuildings.length === 0) return null;

        return (
          <div key={group.key} className="flex flex-col gap-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider px-0.5" style={{ color: COLORS.text.tertiary }}>
              {group.label}
            </div>
            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
              {ownedBuildings.map((b, idx) => {
                const count = p.territory.buildings[b.id] ?? 0;
                const landPct = totalAcres > 0 ? count / totalAcres : 0;
                const bonusFn = BUILDING_BONUS[b.id];
                const bonus = bonusFn ? bonusFn(count, landPct, be) : '—';

                return (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-xs"
                    style={{ borderTop: idx === 0 ? undefined : `1px solid ${COLORS.border}` }}
                  >
                    <span style={{ color: COLORS.text.primary }}>
                      {b.name} <span className="font-mono" style={{ color: COLORS.text.tertiary }}>×{count}</span>
                    </span>
                    <span className="font-mono text-right" style={{ color: COLORS.positive }}>{bonus}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
        Track construction progress on the{' '}
        <Link to="/buildings" style={{ color: COLORS.link }}>Build tab</Link>
        {' '}· Track exploration progress on the{' '}
        <Link to="/explore" style={{ color: COLORS.link }}>Explore page</Link>.
      </div>
    </div>
  );
}
